<template>
  <CustomTable ref="table" :elements=elements :data="data" :searchOptions="searchOptions" :loadData="loadData">
    <template v-slot:header-options>
      <div class="transmission-filters">
        <button class="transmission-filter-button" :class="{ active: isActionFilterActive(true) }" title="Afficher les documents nécessitant une action" @click="setActionFilter(true)">
          <span class="fas fa-triangle-exclamation"></span>Action requise
        </button>

        <button class="transmission-filter-button" :class="{ active: isActionFilterActive(false) }" title="Afficher les documents ne nécessitant aucune action" @click="setActionFilter(false)">
          <span class="fas fa-circle-check"></span>Aucune action
        </button>

        <button class="transmission-filter-button" :class="{ active: isActionFilterActive(null) }" title="Afficher tous les documents" @click="setActionFilter(null)">
          <span class="fas fa-list"></span>Tous
        </button>
      </div>

      <button class="button-icon" title="Actualiser les statuts" @click="refreshAll">
        <span class="fas fa-rotate"></span>
      </button>
    </template>
    <template v-slot:table>
      <thead>
      <tr>
        <custom-header field="documentType" class="hide-for-mobile">Type</custom-header>
        <custom-header field="documentNumber" class="hide-for-mobile">N°Document</custom-header>
        <custom-header field="quoteNumber" class="hide-for-mobile">N°Devis</custom-header>
        <custom-header field="clientName">Client</custom-header>
        <custom-header field="ttcAmount" class="hide-for-mobile">Montant</custom-header>
        <custom-header field="remaining" class="hide-for-mobile">Reste à payer</custom-header>
        <custom-header field="status" class="hide-for-mobile">Status</custom-header>
        <custom-header field="invoiceTransmissionTransmissionStatus">Transmission status</custom-header>
        <custom-header field="actions">action</custom-header>
      </tr>
      </thead>
      <tbody v-if="!isLoading && data && data.length > 0">
      <tr v-for="(business) in $refs.table.$refs.searchArray.simpleFilter(data)" :key="business.documentKey" :class="{hide : !refusedOrAbortedBusinessVisibility && (business.status === 'ABORTED' || business.status === 'REFUSED')}">
        <template v-if="!refusedOrAbortedBusinessVisibility && (business.status === 'ABORTED' || business.status === 'REFUSED')">
          <td :colspan="haveProductionOption ? 9 : 8" class="hide-for-mobile">Devis annulé ou refusé <a @click="this.refusedOrAbortedBusinessVisibility = !this.refusedOrAbortedBusinessVisibility">(afficher)</a></td>
        </template>
        <template v-else>
          <custom-body field="documentType" class="hide-for-mobile">{{ getDocumentTypeLabel(business.documentType) }}</custom-body>
          <custom-body field="documentNumber" class="hide-for-mobile">
            <CustomTooltip :message='business.title ? business.title : ""'>
                <span class="invoice-number">
                  <div v-if="business.documentNumber" class="number-button">
                    <p>{{ formatDocumentNumber(business) }}</p>
                  </div>
                </span>
            </CustomTooltip>
          </custom-body>
          <custom-body field="quoteNumber" class="hide-for-mobile">{{ business.quoteNumber ? business.quoteNumber : '' }}</custom-body>
          <custom-body field="clientName">
            <CustomTooltip :message='business.clientAssociated ? "Client associé" : "Client non associé"'>
                <span class="client-name" :class="{ 'client-associated': business.clientAssociated,'client-not-associated': !business.clientAssociated }">
                  {{ business.currentClientName ? business.currentClientName : (business.clientName ? business.clientName : '') }}
                </span>
            </CustomTooltip>
          </custom-body>
          <custom-body field="ttcAmount" class="hide-for-mobile">{{ business.ttcAmount ? business.ttcAmount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €' : ''}}</custom-body>
          <custom-body field="remaining" class="hide-for-mobile">{{ business.remaining ? business.remaining.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €' : ''}}</custom-body>
          <custom-body field="status" class="hide-for-mobile"><StatusBadge :status="business.status" :status-enum="getStatusEnum(business)" /></custom-body>
          <custom-body field="invoiceTransmissionTransmissionStatus">
            <span class="transmission-status" :title="getTransmissionStatus(business.invoiceTransmissionStatus)" :style="getTransmissionStatusStyle(business.invoiceTransmissionStatus)">
              {{ getTransmissionStatus(business.invoiceTransmissionStatus) }}
            </span>
          </custom-body>
          <custom-body field="actions">
            <button v-if="!business.invoiceTransmissionId && business.clientAssociated && isUploadable(business)" class="button-icon-custom" title="Téléverser" @click="uploadInvoice(business)"><span class="fas fa-arrow-up-from-bracket"></span></button>
            <button v-if="business.invoiceTransmissionId" class="button-icon-custom" title="Actualiser" @click="refreshInvoice(business)"><span class="fas fa-rotate"></span></button>
            <button v-if="business.invoiceTransmissionId" class="button-icon-custom" title="Historique" @click="this.$refs.invoiceTransmissionHistoryModal.openModal(business.association)"><span class="fas fa-clock-rotate-left"></span></button>
            <button v-if="business.invoiceTransmissionId" class="button-icon-custom" title="Gérer le statut" @click="this.$refs.invoiceTransmissionStatusModal.openModal(business.association)"><span class="fas fa-pen-to-square"></span></button>
            <span v-if="canShowPayments(business)" class="payments-action">
              <button class="button-icon-custom" :title="paymentsSummaryTitle(business)" @click="openPaymentsModal(business)"><span class="fas fa-coins"></span></button>
              <span v-if="business.paymentTransmission && business.paymentTransmission.error" class="payments-alert" title="Un paiement n'a pas pu être transmis"></span>
            </span>
          </custom-body>
        </template>
      </tr>
      </tbody>
      <tbody v-else><tr class="empty-row">
        <td colspan="9" class="hide-for-mobile">Aucun contrat enregistré</td>
      </tr></tbody>
    </template>
  </CustomTable>
  <InvoiceTransmissionHistoryModal ref="invoiceTransmissionHistoryModal"/>
  <InvoiceTransmissionStatusModal ref="invoiceTransmissionStatusModal"/>
  <InvoiceTransmissionPaymentsModal ref="invoiceTransmissionPaymentsModal" :callback="loadData"/>
  <InvoiceUploadErrorModal ref="invoiceUploadErrorModal" :callback="loadData"/>
</template>

<script>
import {BUSINESS_STATUS_ENUM} from "@/models/businessStatusEnum";
import {BUSINESS_DOCUMENT_TYPE_ENUM} from "@/models/businessDocumentTypeEnum";
import {CREDIT_NOTE_STATUS_ENUM} from "@/models/creditNoteStatusEnum";
import CustomTable from "@/components/CustomTable.vue";
import CustomHeader from "@/components/tables/CustomHeader.vue";
import CustomBody from "@/components/tables/CustomBody.vue";
import StatusBadge from "@/components/tables/commons/StatusBadge.vue";
import {FILTER_OPERATOR_ENUM} from "@/models/filterOperatorEnum";
import {SORT_OPERATOR_ENUM} from "@/models/sortOperatorEnum";
import {library} from "@fortawesome/fontawesome-svg-core";
import InvoiceTransmissionHistoryModal from "@/invoice-transmission/components/modals/InvoiceTransmissionHistoryModal.vue";
import { InvoiceTransmissionAssociationController} from "@/invoice-transmission/controllers/invoice_transmission/InvoiceTransmissionAssociationController";
import {InvoiceController} from "@/invoice-transmission/controllers/invoice_transmission/InvoiceController";
import {
  getTransmissionStatus,
  getTransmissionStatusStyle, INVOICE_TRANSMISSION_NO_ACTION_REQUIRED_STATUSES,
  INVOICE_TRANSMISSION_STATUS_ENUM
} from "@/invoice-transmission/utils";
import InvoiceTransmissionStatusModal from "@/invoice-transmission/components/modals/InvoiceTransmissionStatusModal.vue";
import InvoiceTransmissionPaymentsModal from "@/invoice-transmission/components/modals/InvoiceTransmissionPaymentsModal.vue";
import {paymentsSummaryTitle} from "@/invoice-transmission/paymentTransmissionUtils";
import CustomTooltip from "@/components/CustomTooltip.vue";
import {faArrowUpFromBracket, faRotate, faClockRotateLeft, faPenToSquare, faTriangleExclamation, faCircleCheck, faList, faCoins} from "@fortawesome/free-solid-svg-icons";
import {CustomTableService} from "@/services/CustomTableService";
import InvoiceUploadErrorModal from "@/invoice-transmission/components/modals/InvoiceUploadErrorModal.vue";
library.add(faArrowUpFromBracket, faRotate, faClockRotateLeft, faPenToSquare, faTriangleExclamation, faCircleCheck, faList, faCoins);

// Valeurs de documentType renvoyées par le back (InvoiceTransmissionDocumentTypeEnum), libellés repris de BUSINESS_DOCUMENT_TYPE_ENUM
const DOCUMENT_TYPE_FILTER_ENUM = {
  INVOICE: {value: "invoice", label: BUSINESS_DOCUMENT_TYPE_ENUM.INVOICE.label},
  DEPOSIT: {value: "deposit", label: BUSINESS_DOCUMENT_TYPE_ENUM.DEPOSIT.label},
  CREDIT_NOTE: {value: "credit-note", label: BUSINESS_DOCUMENT_TYPE_ENUM.CREDIT_NOTE.label},
};

export default {
  name: 'OutgoingInvoiceTransmissionTable',
  computed: {
    BUSINESS_STATUS_ENUM() {
      return BUSINESS_STATUS_ENUM
    }
  },
  components: {
    CustomTooltip,
    StatusBadge,
    InvoiceTransmissionStatusModal,
    InvoiceTransmissionPaymentsModal,
    InvoiceUploadErrorModal,
    CustomBody,
    CustomHeader,
    CustomTable,
    InvoiceTransmissionHistoryModal
  },
  data: function () {
    return {
      data: [],
      elements: [
        {label: "Date du document", value: "documentDate", type: "date"},
        {
          label: "Status PA",
          value: "invoiceTransmissionStatus",
          type: "select",
          values: INVOICE_TRANSMISSION_STATUS_ENUM
        },
        {label: "Type", value: "documentType", type: "select", values: DOCUMENT_TYPE_FILTER_ENUM},
        {label: "N°Document", value: "documentNumber", type: "number"},
        {label: "N°Devis", value: "quoteNumber", type: "number"},
        {label: "Titre", value: "title", type: "text"},
        {label: "Status", value: "status", type: "select", values: BUSINESS_STATUS_ENUM},
        {label: "Client", value: "clientName", type: "text"},
      ],
      searchOptions: {
        filters: [
          // Annulés / refusés, et documents pas encore envoyés (avant SENT) : seuls les statuts SENT et suivants sont transmissibles
          {
            field: "status",
            operator: FILTER_OPERATOR_ENUM.NOT_IN.value,
            variable: [BUSINESS_STATUS_ENUM.REFUSED.value, BUSINESS_STATUS_ENUM.ABORTED.value, BUSINESS_STATUS_ENUM.CREATION.value, BUSINESS_STATUS_ENUM.READY_TO_SEND.value]
          },
          {field: "documentDate", operator: FILTER_OPERATOR_ENUM.GREATER_THAN.value, variable: new Date("09/01/2026")},
          {
            field: "invoiceTransmissionStatus",
            operator: FILTER_OPERATOR_ENUM.NOT_IN.value,
            variable: INVOICE_TRANSMISSION_NO_ACTION_REQUIRED_STATUSES
          }
        ],
        sorts: [
          {field: "documentDate", order: SORT_OPERATOR_ENUM.DESCENDING.value}
        ]
      },
      isLoading: true,
      refusedOrAbortedBusinessVisibility: false,
      currentChoice: undefined
    }
  },
  async mounted() {
    this.$refs.table.$refs.searchArray.initWithURLParams();

    await this.loadData();
  },
  methods: {
    getTransmissionStatusStyle,
    getTransmissionStatus,
    paymentsSummaryTitle,
    getDocumentTypeLabel(documentType) {
      const type = Object.values(DOCUMENT_TYPE_FILTER_ENUM).find(type => type.value === documentType);
      return type ? type.label : '';
    },
    // Paiements reçus d'une facture / d'un acompte envoyé : à déclarer à la plateforme ("Encaissée"), ou déjà suivis (transmis, erreur)
    canShowPayments(business) {
      if (!business.invoiceTransmissionId || business.documentType === DOCUMENT_TYPE_FILTER_ENUM.CREDIT_NOTE.value) return false;
      if (business.association?.providers?.superpdp?.direction !== 'out') return false;
      const counters = business.paymentTransmission;
      return business.paymentCount > 0 || !!(counters && (counters.sent || counters.error || counters.pending));
    },
    openPaymentsModal(business) {
      this.$refs.invoiceTransmissionPaymentsModal.openModal(business.association, this.getDocumentTypeLabel(business.documentType) + ' ' + this.formatDocumentNumber(business));
    },
    isUploadable(business) {
      // Un avoir n'est transmis qu'une fois émis (pas en création ni annulé)
      if (business.documentType === DOCUMENT_TYPE_FILTER_ENUM.CREDIT_NOTE.value)
        return [CREDIT_NOTE_STATUS_ENUM.ISSUED.value, CREDIT_NOTE_STATUS_ENUM.REFUNDED.value].includes(business.status);
      return true;
    },
    formatDocumentNumber(business) {
      // Factures et acomptes partagent la même numérotation (préfixe F), les avoirs ont la leur
      const prefix = business.documentType === DOCUMENT_TYPE_FILTER_ENUM.CREDIT_NOTE.value ? 'A' : 'F';
      return prefix + business.documentNumber.toString().padStart(4, '0');
    },
    getStatusEnum(business) {
      // Les avoirs ont leurs propres statuts, les acomptes n'en ont pas
      return business.documentType === DOCUMENT_TYPE_FILTER_ENUM.CREDIT_NOTE.value ? CREDIT_NOTE_STATUS_ENUM : BUSINESS_STATUS_ENUM;
    },
    getErrorMessage(resp) {
      const errors = resp?.errors;
      if (!errors) return "Une erreur inconnue est survenue lors du téléversement";
      if (typeof errors === 'string') return errors;

      const list = Array.isArray(errors) ? errors : [errors];
      return list.map(error => (typeof error === 'string' ? error : (error.message ?? JSON.stringify(error)))).join('\n');
    },
    async uploadInvoice(business) {
      this.$refs.table?.startLoading();
      let uploadError;
      // Cas où la facture n'a jamais été envoyé, on l'envoie
      if (business.invoiceTransmissionId === undefined) {
        const resp = await InvoiceController.create(business.id, business.documentType, business.creditNoteNumber);
        console.log(resp)
        if (resp && !resp.errors) {
          await this.loadData();
        } else {
          uploadError = this.getErrorMessage(resp);
        }
      }
      this.$refs.table?.stopLoading();

      // Échec : on affiche l'erreur complète, avec la possibilité de recharger les informations du client
      if (uploadError) {
        this.$refs.invoiceUploadErrorModal.openModal(business, this.getDocumentTypeLabel(business.documentType) + ' ' + this.formatDocumentNumber(business), uploadError);
      }
    },
    async loadData() {
      this.$refs.table?.startLoading();
      this.isLoading = true;
      this.data = [];

      const resp = await InvoiceTransmissionAssociationController.getOutgoingInvoiceAssociationTable(this.$refs.table.buildURLParams());

      if (resp && !resp.errors) {
        this.data.push(...resp);
      }

      this.isLoading = false;
      this.$refs.table?.stopLoading();
    },
    async refreshInvoice(business) {
      this.$refs.table?.startLoading();

      const resp = await InvoiceController.refresh(business.association._id);

      if (resp && !resp.errors) {
        await this.loadData();
      }

      this.$refs.table?.stopLoading();
    },
    async refreshAll() {
      this.$refs.table?.startLoading();

      const resp = await InvoiceTransmissionAssociationController.refreshStatuses();

      if (resp && !resp.errors) {
        await this.loadData();
      }

      this.$refs.table?.stopLoading();
    },
    isActionFilterActive(value) {
      const table = CustomTableService.data.get(this.$refs.table?.tableId);

      if (!table) return false;

      const filter = table.currentFilters?.find(
          filter => filter.field === "invoiceTransmissionStatus"
      );

      if (value === null) {
        return !filter;
      }

      if (!filter) return false;

      if (value === true) {
        return filter.operator === FILTER_OPERATOR_ENUM.NOT_IN.value &&
            JSON.stringify(filter.variable) === JSON.stringify(INVOICE_TRANSMISSION_NO_ACTION_REQUIRED_STATUSES);
      }

      return filter.operator === FILTER_OPERATOR_ENUM.IN.value &&
          JSON.stringify(filter.variable) === JSON.stringify(INVOICE_TRANSMISSION_NO_ACTION_REQUIRED_STATUSES);
    },
    setActionFilter(value) {
      const table = CustomTableService.data.get(this.$refs.table.tableId);

      table.currentFilters = table.currentFilters.filter(
          filter => filter.field !== "invoiceTransmissionStatus"
      );

      if (value === true) {
        table.currentFilters.push({
          field: "invoiceTransmissionStatus",
          operator: FILTER_OPERATOR_ENUM.NOT_IN.value,
          variable: INVOICE_TRANSMISSION_NO_ACTION_REQUIRED_STATUSES
        });
      } else if (value === false) {
        table.currentFilters.push({
          field: "invoiceTransmissionStatus",
          operator: FILTER_OPERATOR_ENUM.IN.value,
          variable: INVOICE_TRANSMISSION_NO_ACTION_REQUIRED_STATUSES
        });
      }

      this.loadData();
    }
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

.alert
  color #dc0000
  font-weight bold

.classic
  color auto

.warning
  color #d58900
  font-weight bold

.choice
  display none
  flex-direction column
  position absolute
  right 165px
  margin-top -50px
  padding 5px
  border-radius 6px
  background-color #b9cacf6e

  button
    height 35px
    padding 5px
    padding-right 20px
    padding-left 20px
    margin 5px
    background-color THEME_COLOR_PRIMARY
    color THEME_COLOR_PRIMARY_OPPOSITE
    border none
    border-radius 3px
    font-weight bold
    cursor pointer

  &.choice-displayed
    display flex !important

.fa-file-pdf
  margin-top -2px
  height 27px !important
  width 27px !important

.button-icon
  margin-right 10px

// Bouton des paiements, avec un point rouge quand un paiement n'a pas pu être transmis
.payments-action
  position relative
  display inline-block

.payments-alert
  position absolute
  top 2px
  right 2px
  width 9px
  height 9px
  border-radius 50%
  background #dc0000
  pointer-events none

.number-button
  display flex
  align-items center
  flex 1
  gap 10px

.hide
  height 25px

  td
    text-align center

    a
      cursor pointer

.action-td
  overflow visible
  display flex
  align-items center
  height 40px

.tooltip-container
  margin-left 5px
  position relative
  display flex
  justify-content flex-start

  .tooltip-icon
    width 25px
    height 25px
    color INFO

    &:hover + .tooltip-message
      visibility visible
      opacity 1

  .tooltip-message
    display flex
    flex-direction column
    background-color white
    position absolute
    width 260px
    visibility hidden
    opacity 0
    top -6px
    left -280px
    z-index 10
    box-shadow 0 0 5px 0 rgba(0, 0, 0, .3)
    border-radius 5px
    padding 5px
    transition visibility 0s, opacity .3s linear

  .tooltip-message:hover
    visibility visible
    opacity 1

.transmission-status
  display inline-block
  // Un statut trop long est tronqué par « … » (l'infobulle donne le texte entier) au lieu d'élargir le tableau
  max-width 100%
  overflow hidden
  text-overflow ellipsis
  white-space nowrap
  vertical-align middle
  box-sizing border-box
  padding 4px 10px
  border-radius 20px
  font-size 12px
  font-weight bold

.client-name
  min-width 0
  overflow hidden
  text-overflow ellipsis
  white-space nowrap
  padding 4px 8px
  border-radius 5px

&.client-associated
  background-color #e8f5e9
  color #2e7d32

&.client-not-associated
  background-color #ffebee
  color #c62828

.invoice-number
  display inline-block
  padding 5px 5px
  border-radius 5px
  background-color #eef3f7
  color #45606f
  cursor help

  p
    margin 0
</style>